import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ChartLine, Users, CurrencyDollar, Eye, TrendUp, TrendDown,
  ArrowUp, ArrowDown
} from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const StatCard = ({ title, value, change, icon: Icon, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6"
  >
    <div className="flex items-start justify-between mb-4">
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
        <Icon size={24} weight="duotone" />
      </div>
      {change !== undefined && (
        <div className={`flex items-center gap-1 ${change >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
          {change >= 0 ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
          <span className="text-sm font-medium">{Math.abs(change)}%</span>
        </div>
      )}
    </div>
    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{title}</p>
    <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
  </motion.div>
);

const ChartCard = ({ title, children }) => (
  <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6">
    <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
      {title}
    </h3>
    {children}
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 shadow-lg">
        <p className="text-zinc-400 text-xs mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm font-medium" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Analytics() {
  const [analytics, setAnalytics] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [period, setPeriod] = useState("week");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    try {
      const [summaryRes, chartRes] = await Promise.all([
        axios.get(`${API}/analytics/summary`),
        axios.get(`${API}/analytics/chart-data`, { params: { period } })
      ]);
      setAnalytics(summaryRes.data);
      setChartData(chartRes.data);
    } catch (err) {
      toast.error("Failed to fetch analytics");
    } finally {
      setLoading(false);
    }
  };

  // Transform chart data for Recharts
  const transformedChartData = chartData?.labels?.map((label, index) => ({
    name: label,
    views: chartData.datasets[0]?.data[index] || 0,
    signups: chartData.datasets[1]?.data[index] || 0,
  })) || [];

  // Mock data for other charts
  const platformData = [
    { name: 'TikTok', value: 45 },
    { name: 'Instagram', value: 30 },
    { name: 'YouTube', value: 15 },
    { name: 'LinkedIn', value: 10 },
  ];

  const funnelData = [
    { name: 'Ebook', conversions: 125, visitors: 1200 },
    { name: 'Free Lesson', conversions: 89, visitors: 800 },
    { name: 'Webinar', conversions: 45, visitors: 500 },
    { name: 'Academy', conversions: 23, visitors: 300 },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Analytics
          </h1>
          <p className="text-zinc-400">Track your academy's performance</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40 h-10 bg-zinc-900 border-white/10 rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          title="Total Views"
          value={analytics?.total_visitors?.toLocaleString() || '0'}
          change={12}
          icon={Eye}
          color="bg-cyan-500/20 text-cyan-400"
        />
        <StatCard
          title="New Members"
          value={analytics?.new_members || 0}
          change={23}
          icon={Users}
          color="bg-emerald-500/20 text-emerald-400"
        />
        <StatCard
          title="Revenue"
          value={`$${analytics?.monthly_revenue?.toLocaleString() || '0'}`}
          change={15}
          icon={CurrencyDollar}
          color="bg-amber-500/20 text-amber-400"
        />
        <StatCard
          title="Conversions"
          value={analytics?.total_conversions || 0}
          change={8}
          icon={TrendUp}
          color="bg-violet-500/20 text-violet-400"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Views & Signups Chart */}
        <ChartCard title="Views & Signups">
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={transformedChartData}>
              <defs>
                <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorSignups" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="name" stroke="#52525b" fontSize={12} />
              <YAxis stroke="#52525b" fontSize={12} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="views" stroke="#06b6d4" fillOpacity={1} fill="url(#colorViews)" />
              <Area type="monotone" dataKey="signups" stroke="#f59e0b" fillOpacity={1} fill="url(#colorSignups)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Funnel Performance */}
        <ChartCard title="Funnel Performance">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={funnelData}>
              <XAxis dataKey="name" stroke="#52525b" fontSize={12} />
              <YAxis stroke="#52525b" fontSize={12} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="visitors" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="conversions" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Traffic Sources */}
        <ChartCard title="Traffic by Platform">
          <div className="space-y-4">
            {platformData.map((platform) => (
              <div key={platform.name}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-zinc-300">{platform.name}</span>
                  <span className="text-sm text-zinc-400">{platform.value}%</span>
                </div>
                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full"
                    style={{ width: `${platform.value}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </ChartCard>

        {/* Lesson Performance */}
        <ChartCard title="Top Lessons">
          <div className="space-y-3">
            {[
              { title: 'Introduction to Digital Business', views: 1250 },
              { title: 'Building Your First Funnel', views: 980 },
              { title: 'Social Media Mastery', views: 756 },
              { title: 'Email Marketing Basics', views: 623 },
            ].map((lesson, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="text-sm text-zinc-300 truncate max-w-[180px]">{lesson.title}</span>
                <span className="text-sm text-cyan-400">{lesson.views} views</span>
              </div>
            ))}
          </div>
        </ChartCard>

        {/* Quick Stats */}
        <ChartCard title="Quick Stats">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Total Lessons</span>
              <span className="text-white font-semibold">{analytics?.total_lessons || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Published Lessons</span>
              <span className="text-white font-semibold">{analytics?.published_lessons || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Total Posts</span>
              <span className="text-white font-semibold">{analytics?.total_posts || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Active Funnels</span>
              <span className="text-white font-semibold">{analytics?.active_funnels || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Active Agents</span>
              <span className="text-white font-semibold">{analytics?.active_agents || 0}</span>
            </div>
          </div>
        </ChartCard>
      </div>
    </div>
  );
}
